import React, { useState } from "react";
import { FaGithub, FaGitlab, FaJira, FaSlack } from "react-icons/fa6";
import { Globe } from "lucide-react";

// ─── Types ──────────────────────────────────────────────

export interface StatusEntity {
    key: string;
    name: string;
    subtitle: string;
    roles?: string[];
    activity: number;
    type: "user" | "nhi" | "resource";
    resourceSubType?: string;
    raw: any;
}

// ─── Snapshot IDs (7 days) ───────────────────────────────

export const SNAPSHOT_IDS = ["day-7", "day-6", "day-5", "day-4", "day-3", "day-2", "day-1"];
export const SNAPSHOT_LABELS: Record<string, string> = {
    "day-7": "7d ago",
    "day-6": "6d ago",
    "day-5": "5d ago",
    "day-4": "4d ago",
    "day-3": "3d ago",
    "day-2": "2d ago",
    "day-1": "Today",
};

// ─── Dummy data ─────────────────────────────────────────

export function hashSeed(seed: string): number {
    let h = 0;
    for (let i = 0; i < seed.length; i++) {
        h = (h << 5) - h + seed.charCodeAt(i);
        h |= 0;
    }
    return Math.abs(h);
}

export function getDummyMetrics(key: string, snapshotId: string) {
    const h = hashSeed(key + snapshotId);
    return {
        alertsTriggered: h % 8,
        alertsTriggerable: 3 + (h % 12),
        activity: 10 + (h % 90),
    };
}

export const HIGH_PRIV_ROLES = ["Owner", "Editor", "Security Admin", "IAM Admin", "Compute Admin"];

export const ROLE_POOL = [
    "Owner", "Editor", "Viewer", "Access Approval Approver",
    "Security Admin", "Storage Admin", "Compute Admin",
    "IAM Admin", "Billing Account Viewer", "Logs Viewer",
    "Network Admin", "BigQuery Admin",
];

export function getDummyRoles(key: string, snapshotId: string): string[] {
    const h = hashSeed(key + snapshotId + "roles");
    const count = 2 + (h % 4);
    const roles: string[] = [];
    for (let i = 0; i < count; i++) {
        roles.push(ROLE_POOL[(h + i * 7) % ROLE_POOL.length]);
    }
    return [...new Set(roles)];
}

// ─── Dummy SaaS correlations ────────────────────────────

export const SAAS_APPS_POOL = [
    { name: "GitHub", icon: "github", risk: "high" as const, secrets: 3 },
    { name: "GitLab", icon: "gitlab", risk: "low" as const, secrets: 0 },
    { name: "Jira", icon: "jira", risk: "medium" as const, secrets: 1 },
    { name: "Slack", icon: "slack", risk: "low" as const, secrets: 0 },
];

export function getDummyCorrelations(key: string) {
    const h = hashSeed(key + "corr");
    const count = 1 + (h % 3); // 1-3 apps
    const apps = [];
    for (let i = 0; i < count; i++) {
        const app = SAAS_APPS_POOL[(h + i * 3) % SAAS_APPS_POOL.length];
        apps.push({
            ...app,
            secrets: (h + i) % 5,
            relations: 1 + ((h + i) % 3),
        });
    }
    // Deduplicate
    const unique = apps.filter((a, idx, arr) => arr.findIndex(x => x.name === a.name) === idx);
    const totalRelations = unique.reduce((sum, a) => sum + a.relations, 0);
    const totalSecrets = unique.reduce((sum, a) => sum + a.secrets, 0);
    return { apps: unique, totalRelations, totalSecrets };
}

// ─── Analysis ───────────────────────────────────────────

export interface AnalyzedEntity {
    entity: StatusEntity;
    status: "improved" | "worsened" | "neutral";
    alertDelta: number;
    triggerableDelta: number;
    rolesAdded: string[];
    rolesRemoved: string[];
    highPrivAdded: string[];
    highPrivRemoved: string[];
    snapshotMetrics: { id: string; label: string; triggered: number; triggerable: number }[];
    insights: string[];
    correlations: ReturnType<typeof getDummyCorrelations>;
    potentialRisk: { critical: number; high: number; medium: number; triggerable: number };
}

export function analyzeEntity(entity: StatusEntity): AnalyzedEntity {
    const oldest = SNAPSHOT_IDS[0];
    const latest = SNAPSHOT_IDS[SNAPSHOT_IDS.length - 1];

    const oldMetrics = getDummyMetrics(entity.key, oldest);
    const newMetrics = getDummyMetrics(entity.key, latest);

    const alertDelta = newMetrics.alertsTriggered - oldMetrics.alertsTriggered;
    const triggerableDelta = newMetrics.alertsTriggerable - oldMetrics.alertsTriggerable;

    const oldRoles = entity.type !== "resource" ? getDummyRoles(entity.key, oldest) : [];
    const newRoles = entity.type !== "resource" ? getDummyRoles(entity.key, latest) : [];
    const rolesAdded = newRoles.filter(r => !oldRoles.includes(r));
    const rolesRemoved = oldRoles.filter(r => !newRoles.includes(r));
    const highPrivAdded = rolesAdded.filter(r => HIGH_PRIV_ROLES.includes(r));
    const highPrivRemoved = rolesRemoved.filter(r => HIGH_PRIV_ROLES.includes(r));

    const snapshotMetrics = SNAPSHOT_IDS.map(id => {
        const m = getDummyMetrics(entity.key, id);
        return { id, label: SNAPSHOT_LABELS[id], triggered: m.alertsTriggered, triggerable: m.alertsTriggerable };
    });

    let status: "improved" | "worsened" | "neutral" = "neutral";
    if (highPrivAdded.length > 0 || alertDelta > 0 || triggerableDelta > 2) {
        status = "worsened";
    } else if (highPrivRemoved.length > 0 || alertDelta < 0 || triggerableDelta < -1) {
        status = "improved";
    }

    const insights: string[] = [];
    const label = entity.type === "user" ? "user" : entity.type === "nhi" ? "service account" : "resource";

    if (highPrivAdded.length > 0) {
        insights.push(`High-privilege role(s) added: "${highPrivAdded.join('", "')}". This increases the blast radius if this ${label} is compromised.`);
    }
    if (highPrivRemoved.length > 0) {
        insights.push(`High-privilege role(s) removed: "${highPrivRemoved.join('", "')}". Excellent — this reduces the attack surface.`);
    }
    if (alertDelta > 2) {
        insights.push(`Alerts increased significantly (+${alertDelta}). Investigate recent permission or config changes.`);
    } else if (alertDelta < -2) {
        insights.push(`Alerts decreased by ${Math.abs(alertDelta)}. Remediation efforts are paying off.`);
    }
    if (triggerableDelta > 3) {
        insights.push(`Triggerable alert surface grew by ${triggerableDelta}. Consider tightening permissions.`);
    } else if (triggerableDelta < -2) {
        insights.push(`Triggerable alerts reduced by ${Math.abs(triggerableDelta)}. Good progress on least-privilege.`);
    }
    if (rolesAdded.length > 0 && highPrivAdded.length === 0) {
        insights.push(`${rolesAdded.length} non-critical role(s) added. Verify these align with responsibilities.`);
    }

    const correlations = getDummyCorrelations(entity.key);
    if (correlations.totalSecrets > 0) {
        insights.push(`${correlations.totalSecrets} bound secret(s) detected across ${correlations.apps.length} SaaS app(s). Review access paths.`);
    }

    if (insights.length === 0) {
        insights.push(`${entity.name} is stable across all snapshots. No notable changes detected.`);
    }

    const h2 = hashSeed(entity.key + "risk");
    const potentialRisk = {
        critical: h2 % 3,
        high: 1 + (h2 % 4),
        medium: 2 + (h2 % 6),
        triggerable: newMetrics.alertsTriggerable,
    };

    return {
        entity, status, alertDelta, triggerableDelta, rolesAdded, rolesRemoved,
        highPrivAdded, highPrivRemoved, snapshotMetrics, insights, correlations, potentialRisk,
    };
}

// ─── Smooth spline helper ─────────────────────────────

export function smoothPath(pts: { x: number; y: number }[]): string {
    if (pts.length < 2) return `M ${pts[0]?.x ?? 0},${pts[0]?.y ?? 0}`;
    if (pts.length === 2) return `M ${pts[0].x},${pts[0].y} L ${pts[1].x},${pts[1].y}`;

    // Catmull-Rom to cubic bezier
    let d = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(i - 1, 0)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(i + 2, pts.length - 1)];

        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;

        d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
    }
    return d;
}

// ─── Interactive SVG Charts ─────────────────────────────

export function InteractiveAreaChart({
    values,
    labels,
    color,
    gradientId,
    width = 320,
    height = 140,
}: {
    values: number[];
    labels: string[];
    color: string;
    gradientId: string;
    width?: number;
    height?: number;
}) {
    const [hoverIdx, setHoverIdx] = useState<number | null>(null);
    const svgRef = React.useRef<SVGSVGElement>(null);

    // Guard: need at least 2 data points to draw a chart
    if (!values || values.length < 2) {
        return (
            <div style={{ width, height, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.4, fontSize: 13 }}>
                No data available
            </div>
        );
    }

    const W = width;
    const H = height;

    // Scale factor based on default width of 320
    const scale = W / 320;
    const textScale = 1 + (scale - 1) * 0.5; // Dampen text scaling

    const PAD_LEFT = 32 * scale;
    const PAD_RIGHT = 12 * scale;
    const PAD_TOP = 12 * scale;
    const PAD_BOTTOM = 28 * scale;
    const CHART_W = W - PAD_LEFT - PAD_RIGHT;
    const CHART_H = H - PAD_TOP - PAD_BOTTOM;

    const max = Math.max(...values, 4);
    const min = 0;
    const yTicks = 4;

    const points = values.map((v, i) => {
        const x = PAD_LEFT + (i / (values.length - 1)) * CHART_W;
        const y = PAD_TOP + ((max - v) / (max - min || 1)) * CHART_H;
        return { x, y, v };
    });

    // Smooth Catmull-Rom spline
    const linePath = smoothPath(points);
    const areaPath = `${linePath} L ${points[points.length - 1].x},${PAD_TOP + CHART_H} L ${points[0].x},${PAD_TOP + CHART_H} Z`;

    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        if (!svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const mouseX = ((e.clientX - rect.left) / rect.width) * W;
        // Find closest point
        let closest = 0;
        let minDist = Infinity;
        for (let i = 0; i < points.length; i++) {
            const d = Math.abs(points[i].x - mouseX);
            if (d < minDist) { minDist = d; closest = i; }
        }
        setHoverIdx(closest);
    };

    const handleMouseLeave = () => setHoverIdx(null);

    return (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
            <svg
                ref={svgRef}
                width="100%"
                height="100%"
                viewBox={`0 0 ${W} ${H}`}
                className="cs-interactive-chart"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                preserveAspectRatio="none"
            >
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.2" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.01" />
                    </linearGradient>
                    <filter id={`glow-${gradientId}`}>
                        <feGaussianBlur stdDeviation={2 * scale} result="blur" />
                        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                </defs>

                {/* Y-axis grid lines + labels */}
                {Array.from({ length: yTicks + 1 }).map((_, i) => {
                    const val = Math.round((max / yTicks) * (yTicks - i));
                    const yy = PAD_TOP + (i / yTicks) * CHART_H;
                    return (
                        <g key={i}>
                            <line x1={PAD_LEFT} y1={yy} x2={W - PAD_RIGHT} y2={yy} stroke="hsl(var(--border))" strokeWidth={0.5 * scale} strokeDasharray={i === yTicks ? "0" : `${3 * scale},${3 * scale}`} opacity="0.5" />
                            <text x={PAD_LEFT - 6 * scale} y={yy + 3 * scale} textAnchor="end" fontSize={7 * textScale} fill="hsl(var(--muted-foreground))" opacity="0.5">{val}</text>
                        </g>
                    );
                })}

                {/* X-axis labels */}
                {points.map((p, i) => (
                    <text key={i} x={p.x} y={H - 8 * scale} textAnchor="middle" fontSize={7 * textScale} fontWeight={i === points.length - 1 ? "700" : "400"} fill={i === points.length - 1 ? color : "hsl(var(--muted-foreground))"} opacity={i === points.length - 1 ? 1 : 0.5}>
                        {labels[i]}
                    </text>
                ))}

                {/* Area fill */}
                <path d={areaPath} fill={`url(#${gradientId})`} />

                {/* Main line */}
                <path d={linePath} fill="none" stroke={color} strokeWidth={1 * scale} strokeLinecap="round" strokeLinejoin="round" />

                {/* Default dots */}
                {points.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={hoverIdx === i ? 0 : (i === points.length - 1 ? 3.5 * scale : 2 * scale)} fill={i === points.length - 1 ? color : "hsl(var(--card))"} stroke={color} strokeWidth={i === points.length - 1 ? 0 : 1.5 * scale} opacity={hoverIdx === i ? 0 : 1} style={{ transition: "opacity 0.15s" }} />
                ))}

                {/* Hover crosshair + dot + tooltip */}
                {hoverIdx !== null && (
                    <g>
                        {/* Vertical crosshair line */}
                        <line x1={points[hoverIdx].x} y1={PAD_TOP} x2={points[hoverIdx].x} y2={PAD_TOP + CHART_H} stroke={color} strokeWidth={1 * scale} strokeDasharray={`${4 * scale},${3 * scale}`} opacity="0.35" />

                        {/* Horizontal guide */}
                        <line x1={PAD_LEFT} y1={points[hoverIdx].y} x2={points[hoverIdx].x} y2={points[hoverIdx].y} stroke={color} strokeWidth={0.5 * scale} strokeDasharray={`${3 * scale},${3 * scale}`} opacity="0.25" />

                        {/* Glow ring */}
                        <circle cx={points[hoverIdx].x} cy={points[hoverIdx].y} r={8 * scale} fill={color} opacity="0.1" />
                        <circle cx={points[hoverIdx].x} cy={points[hoverIdx].y} r={4.5 * scale} fill="hsl(var(--card))" stroke={color} strokeWidth={2 * scale} />

                        {/* Tooltip box */}
                        <g>
                            <rect
                                x={points[hoverIdx].x - 28 * scale}
                                y={points[hoverIdx].y - 32 * scale}
                                width={56 * scale}
                                height={22 * scale}
                                rx={6 * scale}
                                fill="hsl(var(--foreground))"
                                opacity="0.9"
                            />
                            <text x={points[hoverIdx].x} y={points[hoverIdx].y - 17.5 * scale} textAnchor="middle" fontSize={11 * textScale} fontWeight="700" fill="hsl(var(--background))">
                                {points[hoverIdx].v} alerts
                            </text>
                        </g>
                    </g>
                )}

                {/* Latest point pulse */}
                {hoverIdx !== points.length - 1 && (
                    <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r={6 * scale} fill={color} opacity="0.12">
                        <animate attributeName="r" values={`${4 * scale};${8 * scale};${4 * scale}`} dur="2s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.15;0.05;0.15" dur="2s" repeatCount="indefinite" />
                    </circle>
                )}
            </svg>
        </div>
    );
}

export function MiniSparkline({ values, color }: { values: number[]; color: string }) {
    const W = 80;
    const H = 28;
    const max = Math.max(...values, 1);

    const points = values.map((v, i) => {
        const x = 2 + (i / (values.length - 1)) * (W - 4);
        const y = 3 + ((max - v) / max) * (H - 6);
        return { x, y };
    });

    const linePath = smoothPath(points);

    return (
        <svg className="cs-card-sparkline" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
            <path d={linePath} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" opacity="0.55" />
            <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="2" fill={color} opacity="0.75" />
        </svg>
    );
}

// ─── SaaS Icon Helper ───────────────────────────────────

export function SaaSIcon({ icon }: { icon: string }) {
    switch (icon) {
        case "github": return <FaGithub />;
        case "gitlab": return <FaGitlab />;
        case "jira": return <FaJira />;
        case "slack": return <FaSlack />;
        default: return <Globe />;
    }
}
